import { AlertProps, useToast } from "@flat-ui";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { AddMembersToTeamResult } from "@store/teams/teams-slice-types";
import { useCallback } from "react";

interface ReturnProps {
  /** Function to respond to user when adding members to company/project/group */
  respondToUser: (
    response:
      | FaroCoreApiTypes.AddMembersToCompanyResponse
      | FaroCoreApiTypes.AddMembersToProjectResponse
      | FaroCoreApiTypes.AddMembersToGroupResponse,
    numberOfMembers: number
  ) => void;

  /** Function to respond to user when adding members to team */
  respondToTeamInvite: (
    response: AddMembersToTeamResult,
    numberOfMembers: number
  ) => void;
}

/** This hook returns functions that checks the backend response and show related toast message based on it */
export function useRespondToInvite(): ReturnProps {
  const { openToast } = useToast();

  const respondToUser = useCallback(
    (
      response:
        | FaroCoreApiTypes.AddMembersToCompanyResponse
        | FaroCoreApiTypes.AddMembersToProjectResponse
        | FaroCoreApiTypes.AddMembersToGroupResponse,
      numberOfMembers: number
    ) => {
      const responseMessage = response.data.message;
      const additionalMessages: string[] = [];
      if (response.errorData) {
        response.errorData.details.failedInvitations?.forEach((error) => {
          additionalMessages.push(error.message);
        });

        response.errorData.details.failedPermissions?.forEach((error) => {
          additionalMessages.push(error.message);
        });
      }

      if (additionalMessages.length === 0) {
        openToast({
          title: "Invitations sent",
          message: responseMessage,
        });
      } else if (additionalMessages.length < numberOfMembers) {
        openToast({
          title: responseMessage,
          message: additionalMessages.join("\n"),
          variant: "warning",
        });
      } else if (additionalMessages.length === numberOfMembers) {
        openToast({
          title: responseMessage,
          message: additionalMessages.join("\n"),
          variant: "error",
        });
      } else {
        throw new Error(
          "Number of additional messages cannot be larger than the actual requests"
        );
      }
    },
    [openToast]
  );

  const respondToTeamInvite = useCallback(
    (response: AddMembersToTeamResult, numberOfMembers: number) => {
      // Show response messages after adding members
      // Errors of creating team is handled by error slice
      const additionalMessages: string[] = [];
      if (response.errorData) {
        response.errorData.forEach((error) => {
          additionalMessages.push(error.message);
        });
      }

      /**
       * Type of toast based on the response status
       * - success: When all the invites go fine
       * - warning: When some of the invites go fine and some are failed
       * - error: When all the invites are failed
       */
      let toastType: AlertProps["variant"] = "success";
      if (additionalMessages.length > 0) {
        toastType =
          additionalMessages.length < numberOfMembers ? "warning" : "error";
      }

      openToast({
        variant: toastType,
        title: "Invitations sent",
        message: additionalMessages.join("\n"),
      });
    },
    [openToast]
  );

  return { respondToUser, respondToTeamInvite };
}
